import moment from 'moment';
import { RemodelingRecord } from '../types';

/**
 * Transform API segment export data to RemodelingRecord format
 * @param exportData - Raw export data from API
 * @returns Transformed RemodelingRecord
 */
export const transformSegmentExportToRemodelingRecord = (
  exportData: any
): RemodelingRecord => {
  return {
    audienceId: exportData.audience_id ?? null,
    exportDate: exportData.created_at || '',
    exportHistory: exportData,
    cpm: exportData.cpm ?? null,
    pom: exportData.percent_of_media ?? null,
    cutSize: exportData.lal_model_precision ?? null,
    seedSize: exportData.seeded_users_count ?? null,
    deliveredSize: exportData.exported_users_count ?? null,
  };
};

/**
 * Transform array of segment exports from API to RemodelingRecord array
 * @param segmentExports - Array of raw export data from API
 * @returns Array of transformed RemodelingRecords
 */
export const transformSegmentExports = (
  segmentExports: any[]
): RemodelingRecord[] => {
  if (!segmentExports || segmentExports.length === 0) return [];
  return segmentExports.map(transformSegmentExportToRemodelingRecord);
};

/**
 * Sort remodeling records by specified column and order
 * @param records - Array of remodeling records to sort
 * @param sortBy - Column key to sort by
 * @param sortOrder - Sort order (ASC or DESC)
 * @returns Sorted array of remodeling records
 */
export const sortRemodelingRecords = (
  records: RemodelingRecord[],
  sortBy: string,
  sortOrder: 'ASC' | 'DESC'
): RemodelingRecord[] => {
  if (!records || records.length === 0) return [];

  const sorted = [...records].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortBy) {
      case 'exportDate':
        aValue = a.exportDate ? new Date(a.exportDate).getTime() : 0;
        bValue = b.exportDate ? new Date(b.exportDate).getTime() : 0;
        break;
      case 'cpm':
        aValue = a.cpm ?? 0;
        bValue = b.cpm ?? 0;
        break;
      case 'pom':
        aValue = a.pom ?? 0;
        bValue = b.pom ?? 0;
        break;
      case 'cutSize':
        aValue = a.cutSize ?? 0;
        bValue = b.cutSize ?? 0;
        break;
      case 'seedSize':
        aValue = a.seedSize ?? 0;
        bValue = b.seedSize ?? 0;
        break;
      case 'deliveredSize':
        aValue = a.deliveredSize ?? 0;
        bValue = b.deliveredSize ?? 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortOrder === 'ASC' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortOrder === 'ASC' ? 1 : -1;
    }
    return 0;
  });

  return sorted;
};

/**
 * Calculate next remodel date based on last exported date, frequency, and enabled status
 * Logic:
 * - If remodel is disabled → return "N/A"
 * - If remodel is enabled → return max(lastExportDate + intervalDays, currentDate)
 * @param lastExportedDate - The last exported date string
 * @param frequencyDays - Number of days between remodels
 * @param enabled - Whether remodeling is enabled
 * @returns Formatted next remodel date string (MM/DD/YYYY) or "N/A"
 */
export const calculateNextRemodelDate = (
  lastExportedDate: string | null,
  frequencyDays: number,
  enabled = true
): string => {
  if (!enabled) {
    return 'N/A';
  }

  const currentDate = moment();

  if (!lastExportedDate) {
    return currentDate.format('MM/DD/YYYY');
  }

  const calculatedNextDate = moment(lastExportedDate).add(
    frequencyDays,
    'days'
  );

  // Return the maximum of calculated next date and current date
  const nextDate = moment.max(calculatedNextDate, currentDate);

  return nextDate.format('MM/DD/YYYY');
};

/**
 * Format schedule frequency as human-readable string
 * @param frequencyDays - Number of days
 * @returns Formatted string (e.g., "every 30 days")
 */
export const formatScheduleLabel = (frequencyDays: number): string => {
  return `every ${frequencyDays} days`;
};

/**
 * Format date string for display
 * @param date - Date string or null
 * @returns Formatted date string (MM/DD/YYYY) or 'N/A'
 */
export const formatDisplayDate = (date: string | null): string => {
  if (!date) {
    return 'N/A';
  }
  return moment(date).format('MM/DD/YYYY');
};
